import moment from 'moment';
import { catchError, from, map, Observable } from 'rxjs';
import { delay, share, switchMap, tap } from 'rxjs/operators';

import {
    HttpContext, HttpContextToken, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor,
    HttpRequest, HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RXJS_DELAY } from '@shared/constants';

import { CacheService } from '../services/core/cache.service';
import { ConfigService } from '../services/core/config.service';
import { environment } from '@environments/environment';

export interface CachedHttpResponse<T> extends HttpResponse<T> {
	dateCached?: Date;
}
export interface ContextOptions {
	cache?: boolean;
	ttl?: number;
	key?: string | ((request: HttpRequest<any>) => string);
	context?: HttpContext;
	override?: boolean;
}
export const CACHE_CONTEXT = new HttpContextToken<ContextOptions>(() => ({}));
export function withAPICache(options: ContextOptions = {}) {
	const { context, ...remainingOptions } = options;
	return (context ?? new HttpContext()).set(CACHE_CONTEXT, {
		cache: true,
		...remainingOptions,
	});
}

export abstract class KeySerializer {
	abstract serialize(request: HttpRequest<any>, context: ContextOptions): string;
}

export class DefaultKeySerializer extends KeySerializer {
	serialize(request: HttpRequest<any>, context: ContextOptions) {
		const { key } = context;

		if (key) {
			return typeof key === 'function' ? key(request) : key;
		}

		return request.urlWithParams;
	}
}

@Injectable()
export class AppHttpInterceptor implements HttpInterceptor {
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(
		private cacheService: CacheService,
		private configService: ConfigService
	) {}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - intercept
	 **-------------------------------------------------------------------------------------
	 */
	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const context = request?.context?.get(CACHE_CONTEXT);
		const store = this.cacheService.apiCache;
		if (request.url.indexOf(environment.rentalsApiEndPoint) > -1 && !context?.override) {
			return next.handle(request);
		}

		return from(Promise.all([this.cacheService.get('secure:accessToken'), this.cacheService.get('secure:idToken'), store.keys()])).pipe(
			switchMap(resp => {
				const [accessToken, idToken, keys] = resp;
				if (request.method === 'GET') {
					if (context?.cache) {
						const generatedKey = this.serialize(request, context);
						const foundKey = keys.find(e => e === generatedKey);
						this.cacheService.setAPICacheTimeout();
						if (foundKey) {
							return from<Observable<HttpEvent<any>>>(store.getItem(foundKey) as any).pipe(
								delay(RXJS_DELAY),
								map((req: any) => {
									req = JSON.parse(req);
									const dateCached = moment(req.dateCached);
									if (moment().diff(dateCached, 'milliseconds') > context.ttl) {
										console.warn('Cache expired for key: ', foundKey);
										store.removeItem(foundKey);
									}
									const cachedResponse: HttpResponse<any> = new HttpResponse(req);
									return cachedResponse.clone();
								}),
								tap(async () => {
									await this.cacheService.isApiCacheExpired();
								})
							);
						}

						const shared = next.handle(request).pipe(
							tap(event => {
								if (event instanceof HttpResponse) {
									const cache = event as CachedHttpResponse<any>;
									cache.dateCached = new Date();
									store.setItem(generatedKey, JSON.stringify(cache));
								}
							}),
							catchError(() => {
								store.removeItem(generatedKey);
								return next.handle(request);
							}),
							share()
						);

						return shared;
					}
				}
				if (request.headers.keys().length) {
					request.headers.append('Authorization', accessToken);
					request.headers.append('idToken', idToken);
					request.headers.append('x-api-key', this.configService.config.apiKey);
					return next.handle(request);
				}
				let headers = new HttpHeaders({
					Authorization: accessToken || '',
					idToken: idToken || '',
					'x-api-key': this.configService.config.apiKey,
				});

				const req = request.clone({
					headers: headers,
				});

				return next.handle(req);
			})
		);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	serialize(request: HttpRequest<any>, context: ContextOptions) {
		const { key } = context;

		if (key) {
			return typeof key === 'function' ? key(request) : key;
		}

		return request.urlWithParams;
	}

	_resolveResponse<T = any>(event: HttpResponse<T>): HttpResponse<T> {
		return event;
	}
}
